// Announcements Management Page
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
    
import AnnouncementModal from './components/AnnouncementModal';
import { FiPlus, FiImage, FiFileText, FiEdit2, FiTrash2 } from 'react-icons/fi';
import Button from '../../../../../components/ui/Button';
import Card from '../../../../../components/ui/Card';
import ConfirmDeleteModal from '../../../../../components/ui/ConfirmDeleteModal';
import Toggle from '../../../../../components/ui/Toggle';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { deleteAnnouncement, fetchAnnouncements, toggleAnnouncementStatus, updateAnnouncement, createAnnouncement } from '../../../../../store/slices/announcementSlice';
import { setHasChanges } from '../../../../../store/slices/branchSlice';
import { Announcement, AnnouncementType } from '../../../../../types/announcement';
import { selectCurrentOrganization } from '../../../../../store/selectors';

const AnnouncementsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { branchId } = useParams<{ branchId: string }>();
  const dispatch = useAppDispatch();

  const { announcements, loading, error } = useAppSelector(
    (state) => state.announcements
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState<string | null>(null);
  const organization = useAppSelector(selectCurrentOrganization);
  const isRTL = i18n.language === 'ar';
  useEffect(() => {
    if (branchId) {
      loadAnnouncements();
    }
  }, [branchId]);

  const loadAnnouncements = () => {
    if (branchId) {
      dispatch(
        fetchAnnouncements({
          branch_id: branchId,
          disable_pagination: true,
        })
      );
    }
  };

  const handleCreateClick = () => {
    setEditingAnnouncement(null);
    setIsModalOpen(true);
  };
  
  const handleEditClick = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setAnnouncementToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (announcementToDelete) {
      await dispatch(deleteAnnouncement(announcementToDelete));
      dispatch(setHasChanges(true));
      setDeleteModalOpen(false);
      setAnnouncementToDelete(null);
      loadAnnouncements();
    }   
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setAnnouncementToDelete(null);
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    await dispatch(toggleAnnouncementStatus({ id, is_active: !currentStatus }));
  };

  const handleModalSave = async (announcementData: Partial<Announcement>) => {
    if (editingAnnouncement?.id) {
      await dispatch(
        updateAnnouncement({
          id: editingAnnouncement.id,
          updates: announcementData,
        })
      );
    } else {
      await dispatch(
        createAnnouncement({
          ...announcementData,
          branch_id: branchId!,
        } as Omit<Announcement, 'id'>)
      );
    }
    dispatch(setHasChanges(true));
    setIsModalOpen(false);
    loadAnnouncements();
  };

  const getTypeColor = (type: AnnouncementType) => {
    switch (type) {
      case 'sale':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'gift':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'event':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'promo':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t('announcements.title', 'Announcements')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('announcements.subtitle', 'Manage your restaurant announcements')}
          </p>
        </div>
      <Button
  onClick={handleCreateClick}
 className="flex flex-row justify-center items-center"
	leftIcon={<FiPlus />}
>

  {t('announcements.create', 'Create Announcement')}
</Button>

      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Announcements List */}
      <Card>
        {loading && announcements.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-500 dark:text-gray-400">
              {t('common.loading', 'Loading...')}
            </p>
          </div>
        ) : announcements.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {t('announcements.noAnnouncements', 'No announcements yet')}
            </p>
            <Button onClick={handleCreateClick} className="mt-4">
              {t('announcements.createFirst', 'Create your first announcement')}
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className={`px-6 py-3  ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>
                    {t('announcements.table.type', 'Type')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>
                    {t('announcements.table.title', 'Title')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>
                    {t('announcements.table.media', 'Media')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>
                    {t('announcements.table.period', 'Period')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>
                    {t('announcements.table.priority', 'Priority')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>
                    {t('announcements.table.status', 'Status')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>
                    {t('announcements.table.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {announcements.map((announcement) => (
                  <tr key={announcement.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(
                          announcement.type
                        )}`}
                      >
                        {isRTL && announcement.ar?.type
                          ? announcement.ar.type
                          : t(`announcements.types.${announcement.type}`, announcement.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {isRTL && announcement.ar?.content?.title
                          ? announcement.ar.content.title
                          : announcement.content.title}
                      </div>
                      {announcement.content.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {isRTL && announcement.ar?.content?.description
                            ? announcement.ar.content.description
                            : announcement.content.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {announcement.media_type === 'image' && announcement.media_url ? (
                          <>
                            <FiImage className="w-5 h-5 text-blue-500" />
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {t('announcements.mediaTypes.image', 'Image')}
                            </span>
                          </>
                        ) : (
                          <>
                            <FiFileText className="w-5 h-5 text-gray-500" />
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {t('announcements.mediaTypes.text', 'Text')}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {formatDate(announcement.start_at)}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(announcement.end_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {announcement.priority || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Toggle
                        enabled={announcement.is_active}
                        onChange={() =>
                          handleToggleStatus(announcement.id!, announcement.is_active)
                        }
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEditClick(announcement)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer"
                        >
                          <FiEdit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(announcement.id!)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 cursor-pointer"
                        >
                          <FiTrash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modals */}
      {isModalOpen && (
        <AnnouncementModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleModalSave}
          announcement={editingAnnouncement}
        />
      )}

      {deleteModalOpen && (
        <ConfirmDeleteModal
          isOpen={deleteModalOpen}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title={t('announcements.deleteTitle', 'Delete Announcement')}
          message={t(
            'announcements.deleteMessage',
            'Are you sure you want to delete this announcement?'
          )}
        />
      )}
    </div>
  );
};

export default AnnouncementsPage;
